import React, { useState, useEffect, useMemo } from 'react';
import { 
  Card, 
  Checkbox, 
  Button, 
  Space, 
  message, 
  Spin, 
  Alert, 
  Row, 
  Col,
  Input,
  Divider,
  Tag,
  Tooltip,
  Empty,
  Badge,
  Breadcrumb,
  Typography
} from 'antd';
import { 
  SearchOutlined, 
  CheckOutlined, 
  CloseOutlined,
  ShopOutlined,
  ArrowLeftOutlined,
  SaveOutlined,
  UserOutlined
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import * as accountService from '../../services/account/accountService';
import * as roleService from '../../services/role/roleService';

const { Title, Text } = Typography;

/**
 * Account Store Data Permission Authorization Page Component
 * @author AI Assistant
 * @description Independent page component for managing account store data permission assignments
 */
const AccountStoreAuthPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { loginId } = useParams();
  
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [account, setAccount] = useState(null);
  const [storePermissionList, setStorePermissionList] = useState([]);
  const [checkedKeys, setCheckedKeys] = useState([]);
  const [originalCheckedKeys, setOriginalCheckedKeys] = useState([]);
  
  // Filter and search state
  const [searchKeyword, setSearchKeyword] = useState('');
  const [regionFilter, setRegionFilter] = useState('');
  const [storeTypeFilter, setStoreTypeFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  // Load account info and data when page loads
  useEffect(() => {
    if (loginId) {
       loadData();
    }
  }, [loginId]);

  const loadData = async () => {
      await loadAccountInfo();
      await loadStorePermissionData();
      await loadAccountStorePermissions();
  };

  // Load account information
  const loadAccountInfo = async () => {
    try {
      // Can call API to get account details here
      // Use loginId as basic info for now
      setAccount({ loginId, name: loginId });
    } catch (error) {
      console.error('Load account info failed:', error);
      message.error(t('account.loadAccountInfoFailed'));
    }
  };

  // Filter store permission data
  const filteredStorePermissions = useMemo(() => {
    let filtered = [...storePermissionList];
    
    // Keyword search
    if (searchKeyword) {
      const keyword = searchKeyword.toLowerCase();
      filtered = filtered.filter(permission => 
        permission.name.toLowerCase().includes(keyword) ||
        permission.code.toLowerCase().includes(keyword)
      );
    }
    
    return filtered;
  }, [storePermissionList, searchKeyword]);

  const loadStorePermissionData = async () => {
    setLoading(true);
    try {
      const response = await roleService.pageRoles({ 
        type: "DATA", 
        currentPage: 1,
        pageSize: 1000 
      });
      if (response.success && response.data) {
        setStorePermissionList(response.data.tableData || []);
      } else {
        message.error(response.errorMessage || t('account.loadStorePermissionsFailed'));
      }
    } catch (error) {
      console.error('Load store permissions failed:', error);
      message.error(t('account.loadStorePermissionsFailedRetry'));
    } finally {
      setLoading(false);
    }
  };

  const loadAccountStorePermissions = async () => {
    if (!loginId) return;
    
    console.log('Start loading account store permissions:', loginId);
    
    try {
      const response = await roleService.pageRoles({
        type: "DATA",
        authQueryType: "1", 
        authedAccount: loginId,
        currentPage: 1,
        pageSize: 1000
      });
      console.log('Account data permission roles response:', response);  
      
      if (response.success && response.data) {
        const permissionCodes = Array.isArray(response.data.tableData) 
          ? response.data?.tableData?.map(permission => permission.code) 
          : [];
        console.log('Store permission codes obtained:', permissionCodes);
        setCheckedKeys(permissionCodes);
        setOriginalCheckedKeys([...permissionCodes]);
      } else {
        console.warn('Failed to get account store permissions:', response.errorMessage);
        setCheckedKeys([]);
        setOriginalCheckedKeys([]);
      }
    } catch (error) {
      console.error('Load account store permissions failed:', error);
      setCheckedKeys([]);
      setOriginalCheckedKeys([]);
    }
  };

  const mapStoreToPermission = (storeCode) => {
    return "Store" + storeCode + " Data Permission";
  };
  
  // Check handler for checkbox group
  const onCheck = (checkedValues) => {
    setCheckedKeys(checkedValues);
  };

  // Select all store permissions
  const selectAll = () => {
    const allKeys = storePermissionList.map(permission => permission.code);
    setCheckedKeys(allKeys);
  };

  // Clear all store permissions
  const clearAll = () => {
    setCheckedKeys([]);
  };

  // Check if there are changes
  const hasChanges = () => {
    return JSON.stringify(checkedKeys.sort()) !== JSON.stringify(originalCheckedKeys.sort());
  };

  // Calculate change statistics
  const getChangeStats = () => {
    const addedPermissions = checkedKeys.filter(key => !originalCheckedKeys.includes(key));
    const removedPermissions = originalCheckedKeys.filter(key => !checkedKeys.includes(key));
    const totalChanges = addedPermissions.length + removedPermissions.length;
    
    return {
      added: addedPermissions.length,
      removed: removedPermissions.length,
      total: totalChanges,
      hasChanges: totalChanges > 0
    };
  };

  // Save store permission assignments
  const handleSave = async () => {
    if (!loginId) {
      console.error('Account info incomplete:', loginId);
      message.error(t('account.accountInfoIncomplete'));
      return;
    }
    
    if (!getChangeStats().hasChanges) {
      message.info(t('account.noChangesToSave'));
      return;
    }
    
    // Calculate added and removed permissions
    const originalSet = new Set(originalCheckedKeys);
    const currentSet = new Set(checkedKeys);
    
    // Find added permissions (selected but not in original list)
    const addedPermissions = checkedKeys.filter(code => !originalSet.has(code));
    
    // Find removed permissions (in original list but not selected)
    const removedPermissions = originalCheckedKeys.filter(code => !currentSet.has(code));
    
    console.log('Store permission changes:', {
      loginId: loginId,
      addedPermissions: addedPermissions,
      removedPermissions: removedPermissions,
      originalPermissionCodes: originalCheckedKeys,
      currentPermissionCodes: checkedKeys
    });
    
    setSaving(true);
    try {
      // Process added permissions
      if (addedPermissions.length > 0) {
        const addResponse = await accountService.assignRoles({
          loginId: loginId,
          roleCodeSet: addedPermissions
        });
        
        if (!addResponse || !addResponse.success) {
          console.error('Assign store permissions failed:', addResponse);
          message.error(addResponse?.errorMessage || t('account.assignStorePermissionsFailed'));
          return;
        }
      }
      
      // Process removed permissions
      if (removedPermissions.length > 0) {
        const removeResponse = await accountService.removeRoles({
          loginId: loginId,
          removeRoleCodeSet: removedPermissions
        });
        
        if (!removeResponse || !removeResponse.success) {
          console.error('Remove roles failed:', removeResponse);
          message.error(removeResponse?.errorMessage || t('account.removeRolesFailed'));
          return;
        }
      }
      
      message.success(t('account.assignStorePermissionsSuccess'));
      // Update original state
      setOriginalCheckedKeys([...checkedKeys]);
    } catch (error) {
      console.error('Save store permissions failed:', error);
      message.error(t('account.assignStorePermissionsFailed'));
    } finally {
      setSaving(false);
    }
  };

  // Go back to account management
  const handleGoBack = () => {
    navigate('/account-management');
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <Spin size="large" />
        <div style={{ marginTop: 16 }}>
          {t('account.loadingStorePermissions')}
        </div>
      </div>
    );
  }

  return (
    <div style={{ padding: '24px', minHeight: '100vh', backgroundColor: '#f5f5f5' }}>
      {/* 页面头部 */}
      <div style={{ marginBottom: 24 }}>
        <Breadcrumb style={{ marginBottom: 16 }}>
          <Breadcrumb.Item>
            <a onClick={handleGoBack} style={{ color: '#1890ff' }}>
              {t('account.title')}
            </a>
          </Breadcrumb.Item>
          <Breadcrumb.Item>{t('account.storePermissionAuthorization')}</Breadcrumb.Item>
        </Breadcrumb>
        
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <Button 
              icon={<ArrowLeftOutlined />} 
              onClick={handleGoBack}
              style={{ marginRight: 16 }}
            >
              {t('common.back')}
            </Button>
            <div>
              <Title level={3} style={{ margin: 0, display: 'flex', alignItems: 'center' }}>
                <UserOutlined style={{ marginRight: 8, color: '#1890ff' }} />
                {t('account.storePermissionAuthorizationTitle', { accountName: account?.name || loginId })}
              </Title>
              <Text type="secondary">
                {t('account.storePermissionAuthorizationDesc')}
              </Text>
            </div>
          </div>
          
          <Space>
            <Button 
              type="primary" 
              icon={<SaveOutlined />}
              loading={saving}
              onClick={handleSave}
              disabled={!getChangeStats().hasChanges}
              size="large"
            >
              {t('common.save')}
            </Button>
          </Space>
        </div>
      </div>

      <Row gutter={24}>
        <Col span={24}>
          {/* 当前权限显示区域 */}
          <Card 
            title={
              <Space>
                <ShopOutlined style={{ color: '#1890ff' }} />
                {t('account.currentStorePermissions')}
              </Space>
            }
            style={{ marginBottom: 24 }}
            extra={
              <Space>
                <Badge count={originalCheckedKeys.length} showZero color="#52c41a" />
                <span style={{ fontSize: 12, color: '#666' }}>
                  {t('account.totalPermissions', { count: originalCheckedKeys.length })}
                </span>
              </Space>
            }
          >
            {originalCheckedKeys.length === 0 ? (
              <Empty 
                description={t('account.noCurrentStorePermissions')} 
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                style={{ margin: '40px 0' }}
              />
            ) : (
              <div style={{ 
                maxHeight: 200, 
                overflowY: 'auto',
                padding: '8px 0'
              }}>
                <Row gutter={[12, 12]}>
                  {originalCheckedKeys.map(permissionCode => {
                    const permission = storePermissionList.find(p => p.code === permissionCode);
                    const isStillSelected = checkedKeys.includes(permissionCode);
                    
                    return (
                      <Col span={12} key={permissionCode}>
                        <div style={{
                          display: 'flex',
                          alignItems: 'center',
                          padding: '8px 12px',
                          borderRadius: 6,
                          backgroundColor: isStillSelected ? '#f6ffed' : '#fff2e8',
                          border: isStillSelected ? '1px solid #b7eb8f' : '1px solid #ffd591',
                          fontSize: 13
                        }}>
                          <div style={{ 
                            width: 8, 
                            height: 8, 
                            borderRadius: '50%', 
                            backgroundColor: isStillSelected ? '#52c41a' : '#fa8c16',
                            marginRight: 10,
                            flexShrink: 0
                          }} />
                          <div style={{ flex: 1 }}>
                            <div style={{ 
                              fontWeight: 500, 
                              color: isStillSelected ? '#52c41a' : '#fa8c16'
                            }}>
                              {permission ? permission.name : permissionCode}
                            </div>
                            <div style={{ color: '#999', fontSize: 11, marginTop: 2 }}>
                              {t('dataPermission.code')}: {permissionCode}
                            </div>
                          </div>
                          <div style={{ 
                            marginLeft: 8,
                            fontSize: 12,
                            color: isStillSelected ? '#52c41a' : '#fa8c16',
                            flexShrink: 0
                          }}>
                            {isStillSelected ? (
                              <Tooltip title={t('account.permissionKept')}>
                                <CheckOutlined />
                              </Tooltip>
                            ) : (
                              <Tooltip title={t('account.permissionWillBeRemoved')}>
                                <CloseOutlined />
                              </Tooltip>
                            )}
                          </div>
                        </div>
                      </Col>
                    );
                  })}
                </Row>
              </div>
            )}
          </Card>

          {/* 操作统计 */}
          <Card style={{ marginBottom: 24 }}>
            <Row gutter={24}>
              <Col span={6}>
                <div style={{ textAlign: 'center' }}>
                  <Badge count={checkedKeys.length} showZero color="#52c41a" style={{ fontSize: 16 }} />
                  <div style={{ marginTop: 8, fontWeight: 500 }}>
                    {t('account.selectedStorePermissionCount', { count: checkedKeys.length })}
                  </div>
                </div>
              </Col>
              <Col span={6}>
                <div style={{ textAlign: 'center' }}>
                  <Badge count={storePermissionList.length} showZero color="#1890ff" style={{ fontSize: 16 }} />
                  <div style={{ marginTop: 8, fontWeight: 500 }}>
                    {t('account.totalStorePermissionCount', { count: storePermissionList.length })}
                  </div>
                </div>
              </Col>
              <Col span={6}>
                <div style={{ textAlign: 'center' }}>
                  <Badge count={getChangeStats().total} showZero color={getChangeStats().hasChanges ? "#fa8c16" : "#52c41a"} style={{ fontSize: 16 }} />
                  <div style={{ marginTop: 8, fontWeight: 500 }}>
                    {getChangeStats().hasChanges ? t('account.hasChanges') : t('account.noChanges')}
                  </div>
                  {getChangeStats().hasChanges && (
                    <div style={{ fontSize: 12, color: '#666', marginTop: 4 }}>
                      +{getChangeStats().added} -{getChangeStats().removed}
                    </div>
                  )}
                </div>
              </Col>
              <Col span={6}>
                <div style={{ textAlign: 'center' }}>
                  <Space>
                    <Button onClick={selectAll} disabled={saving}>
                      {t('account.selectAll')}
                    </Button>
                    <Button onClick={clearAll} disabled={saving}>
                      {t('account.clearAll')}
                    </Button>
                  </Space>
                </div>
              </Col>
            </Row>
          </Card>

          {/* 变更提示 */}
          {getChangeStats().hasChanges && (
            <Alert
              message={
                <div>
                  <div>{t('account.storePermissionsChanged')}</div>
                  <div style={{ marginTop: 4, fontSize: 12 }}>
                    {getChangeStats().added > 0 && (
                      <span style={{ color: '#52c41a' }}>
                        {t('account.addedPermissions', { count: getChangeStats().added })}
                      </span>
                    )}
                    {getChangeStats().added > 0 && getChangeStats().removed > 0 && (
                      <span style={{ margin: '0 8px', color: '#999' }}>|</span>
                    )}
                    {getChangeStats().removed > 0 && (
                      <span style={{ color: '#fa8c16' }}>
                        {t('account.removedPermissions', { count: getChangeStats().removed })}
                      </span>
                    )}
                  </div>
                </div>
              }
              type="warning"
              showIcon
              style={{ marginBottom: 24 }}
            />
          )}

          {/* 权限分配 */}
          <Card>
            {/* 搜索功能 */}
            <Card size="small" style={{ marginBottom: 16 }}>
              <Row gutter={[16, 8]}>
                <Col span={16}>
                  <Input
                    placeholder={t('account.searchStorePermissions')}
                    prefix={<SearchOutlined />}
                    value={searchKeyword}
                    onChange={(e) => setSearchKeyword(e.target.value)}
                    allowClear
                  />
                </Col>
                <Col span={8}>
                  <div style={{ textAlign: 'right', color: '#666', fontSize: '12px', paddingTop: '6px' }}>
                    {t('account.filteredCount', { count: filteredStorePermissions.length })}
                  </div>
                </Col>
              </Row>
            </Card>

            {/* 门店权限列表 */}
            <div style={{ maxHeight: 600, overflowY: 'auto', border: '1px solid #d9d9d9', padding: 16, borderRadius: 6 }}>
              {filteredStorePermissions.length === 0 ? (
                <Empty 
                  description={searchKeyword ? t('account.noFilteredResults') : t('account.noStorePermissionsAvailable')} 
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                />
              ) : (
                <Checkbox.Group
                  value={checkedKeys}
                  onChange={onCheck}
                  style={{ width: '100%' }}
                  disabled={saving}
                >
                  <Row gutter={[16, 8]}>
                    {filteredStorePermissions.map((permission) => (
                      <Col span={12} key={permission.code}>
                        <Checkbox value={permission.code} style={{ width: '100%' }}>
                          <div style={{ marginLeft: 8 }}>
                            <div style={{ fontWeight: 500, display: 'flex', alignItems: 'center' }}>
                              <ShopOutlined style={{ marginRight: 4, color: '#1890ff' }} />
                              {permission.name}
                            </div>
                            <div style={{ fontSize: 12, color: '#666', marginTop: 2 }}>
                              {t('dataPermission.code')}: {permission.code}
                            </div>
                          </div>
                        </Checkbox>
                      </Col>
                    ))}
                  </Row>
                </Checkbox.Group>
              )}
            </div>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default AccountStoreAuthPage;